import type { QueryKey } from '@tanstack/vue-query'
import { MutationCache, QueryCache, QueryClient, VueQueryPlugin } from '@tanstack/vue-query'
import { h } from 'vue'
import { toast } from 'vue-sonner'

/**
 * The API answers 403 with meta.message "No Access." (missing privilege) or
 * "Invalid team member." (not a connected member of this business). useApi()
 * unwraps responses, so the payload may arrive as the error itself or under
 * `response.data` — check both shapes.
 */
function isNoAccessError(error: any): boolean {
  const status = error?.response?.status ?? error?.meta?.code ?? error?.status
  const message = String(
    error?.response?.data?.meta?.message ?? error?.meta?.message ?? error?.message ?? '',
  )
  return status === 403 || /no access|invalid team member/i.test(message)
}

export default defineNuxtPlugin((nuxtApp) => {
  const queryClient = new QueryClient({
    // Only privilege failures are surfaced here; other query errors keep their
    // existing behaviour (handled per-page / empty state).
    queryCache: new QueryCache({
      onError: (error: any, query: any) => {
        if (!isNoAccessError(error))
          return
        // Capability probes (e.g. balance/disbursement/config) answer 403 when the
        // role lacks the privilege. That is an expected answer, not a failure - the
        // UI just hides the action. Toasting it would nag view-only users on every
        // page load.
        if (query?.meta?.silentNoAccess)
          return
        toast.error('Tidak ada akses', {
          duration: 4000,
          description: 'Anda tidak memiliki akses untuk melihat data ini. Hubungi admin bisnis Anda.',
        })
      },
    }),
    mutationCache: new MutationCache({
      onSuccess: (_data, _variables, _context, mutation) => {
        const successMessage = mutation.meta?.successMessage as string | undefined
        if (successMessage) {
          toast.success('Berhasil', {
            duration: 3000,
            description: successMessage,
          })
        }
      },
      onError: (error: any, _variables, _context, mutation) => {
        // Privilege errors get a plain-language message instead of the generic
        // "Gagal ..." toast. The UI hides actions the role cannot perform, but a
        // role can be changed mid-session, so this is the safety net for that gap.
        if (isNoAccessError(error)) {
          toast.error('Tidak ada akses', {
            duration: 4000,
            description: 'Anda tidak memiliki akses untuk tindakan ini. Hubungi admin bisnis Anda.',
          })
          return
        }

        const errorMessage = mutation.meta?.errorMessage as string | undefined
        const errorDynamic = error?.meta?.message || error?.message
        if (error?.data && typeof error.data === 'object') {
          const validationErrors: string[] = []
          Object.values(error.data).forEach((fieldErrors: any) => {
            if (Array.isArray(fieldErrors)) {
              validationErrors.push(...fieldErrors)
            }
          })
          if (validationErrors.length > 0) {
            const description = h('div', { class: 'space-y-1' }, validationErrors.map((msg, index) =>
              h('div', { key: index }, `${index + 1}. ${msg}`),
            ))
            toast.error('Gagal', {
              duration: 4000,
              description,
            })
            return
          }
        }

        if (errorMessage) {
          toast.error(errorMessage)
        }
        else if (errorDynamic) {
          toast.error('Gagal', {
            duration: 2000,
            description: errorDynamic || 'Terjadi kesalahan',
          })
        }
      },
      onSettled: (data, _error, _variables, _context, mutation) => {
        const invalidateQueries = mutation.meta?.invalidateQueries as QueryKey | undefined
        const invalidateCondition = mutation.meta?.invalidateCondition as ((data: any) => boolean) | undefined
        const shouldInvalidate = invalidateCondition ? invalidateCondition(data) : true
        if (invalidateQueries && shouldInvalidate) {
          queryClient.invalidateQueries({ queryKey: invalidateQueries })
        }
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 1000 * 60 * 5,
        refetchOnWindowFocus: false,
        retry: 1,
      },
    },
  })

  nuxtApp.vueApp.use(VueQueryPlugin, {
    queryClient,
  })

  return {
    provide: {
      queryClient,
    },
  }
})
